/**
 * Moneda de visualización (₲ PYG / R$ BRL / US$ USD).
 *
 * Los precios del producto se guardan y se cobran SIEMPRE en guaraníes (PYG):
 * es la moneda base. Este módulo solo controla en qué moneda se *muestran* al
 * usuario, convirtiendo con las tasas del backend. El monto real de pago no
 * cambia (por eso donde corresponde aclaramos "se factura en ₲").
 *
 * - `Currency`: las tres monedas soportadas.
 * - `useExchangeRates()`: tasas públicas (`GET /exchange-rates`), cacheadas.
 * - `CurrencyProvider` / `useCurrency()`: moneda elegida + persistencia.
 * - `formatPrice(pygAmount, currency, rates)`: convierte desde ₲ y formatea con
 *   el símbolo y los separadores correctos por moneda.
 *
 * Default: si el usuario no eligió moneda, se deriva del idioma (es→PYG,
 * pt→BRL). Si eligió, se respeta y se persiste en localStorage; y al cambiar el
 * idioma se sigue respetando su elección manual.
 */
import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from './api';
import { getLocale, subscribeLocale, type Locale } from './i18n';

export type Currency = 'PYG' | 'BRL' | 'USD';

export const CURRENCIES: Currency[] = ['PYG', 'BRL', 'USD'];

/** Símbolo de cada moneda (según el uso local: ₲ / R$ / US$). */
export const CURRENCY_SYMBOL: Record<Currency, string> = {
  PYG: '₲',
  BRL: 'R$',
  USD: 'US$',
};

/**
 * Tasas de cambio: `rates[X]` = cuántos ₲ vale 1 unidad de X (PYG = 1).
 * Shape del contrato `GET /exchange-rates`.
 */
export interface ExchangeRates {
  base: string;
  rates: Record<Currency, number>;
  updated_at: string;
}

const STORAGE_KEY = 'tembiapo:currency';

/** Default de moneda por idioma cuando el usuario no eligió manualmente. */
export function defaultCurrencyForLocale(locale: Locale): Currency {
  return locale === 'pt' ? 'BRL' : 'PYG';
}

function readStoredCurrency(): Currency | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'PYG' || v === 'BRL' || v === 'USD' ? v : null;
  } catch {
    return null;
  }
}

// --- Tasas (query pública, cacheada) ----------------------------------------

const RATES_KEY = ['exchange-rates'] as const;

/** Tasas de cambio públicas. Cambian poco → staleTime largo. */
export function useExchangeRates() {
  return useQuery({
    queryKey: RATES_KEY,
    queryFn: () => api.get<ExchangeRates>('/exchange-rates'),
    staleTime: 30 * 60_000,
    retry: 1,
  });
}

// --- Formateo ----------------------------------------------------------------

/**
 * Convierte un importe en ₲ a la moneda elegida y lo formatea.
 *
 * - PYG: sin decimales, miles con punto (₲150.000).
 * - BRL: 2 decimales, formato pt-BR (R$1.234,56).
 * - USD: 2 decimales, formato en-US (US$1,234.56).
 *
 * Si las tasas todavía no cargaron (o falta la de esa moneda), cae a mostrar el
 * importe en ₲ para no presentar un número convertido incorrecto.
 */
export function formatPrice(
  pygAmount: number,
  currency: Currency,
  rates?: ExchangeRates,
): string {
  const rate = currency === 'PYG' ? 1 : rates?.rates?.[currency];
  // Sin tasa disponible para una moneda extranjera → fallback a ₲.
  const effective: Currency = rate && rate > 0 ? currency : 'PYG';
  const effRate = effective === 'PYG' ? 1 : (rate as number);
  const amount = pygAmount / effRate;

  if (effective === 'PYG') {
    const n = Math.round(amount);
    return `₲${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(n)}`;
  }
  if (effective === 'BRL') {
    return `R$${new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)}`;
  }
  return `US$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

// --- Contexto / estado -------------------------------------------------------

interface CurrencyContextValue {
  /** Moneda de visualización activa. */
  currency: Currency;
  /** Fija la moneda (elección manual → se persiste). */
  setCurrency: (c: Currency) => void;
  /** true si el usuario la fijó manualmente (vs. default por idioma). */
  isManual: boolean;
  /** Tasas de cambio (undefined mientras cargan o si falló la carga). */
  rates?: ExchangeRates;
  /** Atajo: formatea un importe en ₲ con la moneda y tasas actuales. */
  format: (pygAmount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  // Estado inicial: elección persistida si existe; si no, default por idioma.
  const [stored, setStored] = useState<Currency | null>(() => readStoredCurrency());
  const [localeCurrency, setLocaleCurrency] = useState<Currency>(() =>
    defaultCurrencyForLocale(getLocale()),
  );

  // Si el usuario no fijó moneda, seguimos el default por idioma en caliente.
  useEffect(() => {
    return subscribeLocale((locale) => {
      setLocaleCurrency(defaultCurrencyForLocale(locale));
    });
  }, []);

  const { data: rates } = useExchangeRates();

  const currency: Currency = stored ?? localeCurrency;

  const setCurrency = (c: Currency) => {
    setStored(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // localStorage no disponible (modo privado / SSR): degradamos a memoria.
    }
  };

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      setCurrency,
      isManual: stored !== null,
      rates,
      format: (pygAmount: number) => formatPrice(pygAmount, currency, rates),
    }),
    [currency, stored, rates],
  );

  return createElement(CurrencyContext.Provider, { value }, children);
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency debe usarse dentro de <CurrencyProvider>');
  }
  return ctx;
}
